/**
 * ErrorNotice - the one error banner (WS-9 Core wave 1).
 *
 * Renders a registry entry's four answers (what happened / is my work
 * safe / what do I do / who fixes it) as today's role="alert" banner;
 * same visual, no new design language. The copy comes from
 * src/lib/errorCopy.ts; the escalation contact comes from the
 * `tenants.config.support.contact` seam (useSupportContact).
 *
 * Inline single-string slots (composer status line, feed rows,
 * RefinePanel's error prop) use errorText() from the registry directly;
 * this component is the banner edition for form/page surfaces.
 */

import { type ReactNode } from 'react';
import { errorText, type ErrorSurface } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

export interface ErrorNoticeProps {
  surface: ErrorSurface;
  /** Registry key ('rate_limited', 'max_tokens', ...); null/unknown
   *  renders the honest needs-platform generic. */
  code: string | null;
  /** Copy interpolations ({limit}, ...) for entries that use them. */
  vars?: Record<string, string>;
  'data-testid'?: string;
}

const BANNER_CLS =
  'rounded-[12px] border border-warm-200 bg-warm-50 px-3 py-2 text-sm text-warm-700';

export function ErrorNotice({
  surface,
  code,
  vars,
  'data-testid': testId,
}: ErrorNoticeProps): ReactNode {
  const contact = useSupportContact();
  return (
    <div role="alert" className={BANNER_CLS} data-testid={testId}>
      {errorText(surface, code, contact, vars)}
    </div>
  );
}
